import React from 'react';
import { COLUMN_NAME_MAX_LENGTH, useViewColumnsValidator } from '../validation';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import {
  dataModelerSelectCurrentDraftEdits,
  dataModelerSelectCurrentSchemaObj,
} from '../../../../redux/selectors/dataModeler';
import { ICdwTableColumnData } from './types';
import { DataModelerSelectViewExtendFieldKey } from '../DataModelerFieldDropdown';
import {
  dataModelerSwitchDraftFieldToAddInput,
  dataModelerSwitchDraftFieldToExtendInput,
} from '../../../../redux/slices/dataModeler';
import { ISelectableFieldItem } from '../DataModelerFieldDropdown/utils';
import { MapToFieldDiv } from './DataModelerCdwTableGridUI';
import KubitTooltip from '../../../../components/KubitTooltip';
import { EditableTextInput } from '../../../../components/Form';

interface IProps {
  isEditMode: boolean;
  tableKey: string;
  viewKey: string;
  columnData: ICdwTableColumnData;
  menuUp: boolean;
}

const CdwTableGridRowMapToField = ({
  isEditMode,
  tableKey,
  viewKey,
  columnData,
  menuUp,
}: IProps) => {
  const dispatch = useAppDispatch();
  const {
    fieldName,
    setFieldName,
    tableColumn,
    isExtend,
    isRequired,
    isNew,
    unionCount,
  } = columnData;

  const schemaObj = useAppSelector(dataModelerSelectCurrentSchemaObj);
  const draftEdits = useAppSelector(dataModelerSelectCurrentDraftEdits);
  const isFactTable = schemaObj?.fact?.key === viewKey;
  const numFactTables =
    (schemaObj?.fact?.tableKeys?.length ?? 0) +
    (draftEdits?.addedFactsData?.length ?? 0);
  const { namesValidator } = useViewColumnsValidator({
    tableKey,
    viewKey,
    tableColumn: tableColumn.column,
  });

  const handleExtendSelectChange = (selectedItem: ISelectableFieldItem) => {
    if (selectedItem.justCreated) {
      dispatch(
        dataModelerSwitchDraftFieldToAddInput({
          tableKey,
          column: columnData.tableColumn.column,
          fieldName,
        }),
      );
    } else {
      dispatch(
        dataModelerSwitchDraftFieldToExtendInput({
          tableKey,
          column: columnData.tableColumn.column,
          fieldKey: selectedItem.fieldKey,
        }),
      );
    }
  };

  const handleNewFieldNameSet = (newName: string) => {
    if (isExtend) {
      dispatch(
        dataModelerSwitchDraftFieldToAddInput({
          tableKey,
          column: columnData.tableColumn.column,
          fieldName: newName,
        }),
      );
    } else {
      setFieldName(newName);
    }
  };

  if (
    !isEditMode ||
    !isFactTable ||
    numFactTables < 2 ||
    (!isNew && !isExtend)
  ) {
    return (
      <MapToFieldDiv>
        <EditableTextInput
          disabled={!isEditMode || !isNew}
          placeholder={'Enter name'}
          inputSize={'XS'}
          value={fieldName}
          onSubmit={setFieldName}
          limit={COLUMN_NAME_MAX_LENGTH}
          validation={namesValidator}
        />
        {unionCount > 1 && (
          <KubitTooltip
            content={`This column is extending a UNION field. ${unionCount} columns total.`}
            noOpenDelay={true}
          >
            <div>(union)</div>
          </KubitTooltip>
        )}
      </MapToFieldDiv>
    );
  }

  return (
    <MapToFieldDiv>
      <DataModelerSelectViewExtendFieldKey
        tableKey={tableKey}
        columnData={columnData}
        onChange={handleExtendSelectChange}
        onNewFieldName={handleNewFieldNameSet}
        isRequired={isRequired || !isEditMode}
        menuPlacement={menuUp ? 'top' : 'auto'}
      />
    </MapToFieldDiv>
  );
};

export default CdwTableGridRowMapToField;
